import { isAxiosError } from "axios";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  Caracteristicas,
  Combinacoes,
  Detalhes,
} from "shared/components/etapasCadastro/Produto";
import PageContainer from "shared/components/pagecontainer/PageContainer";
import Tabs from "shared/components/tabs/Tabs";
import { IPicture } from "shared/interfaces/IPicture";
import { buscarLojasPorUsuario } from "shared/services/api/lojas.api";
import {
  atualizarProduto,
  buscarMeuProdutoPorId,
  sincronizarCombinacoesProduto,
} from "shared/services/api/produto.api";
import {
  listarCombinacoes,
  toSelectedVariationOptions,
  type ProductCombinationInput,
} from "shared/services/api/combinacoes.api";
import type { CombinationOption } from "shared/services/api/combinacoes.api";
import { uploadImagemProduto } from "shared/services/api/uploadImagem.api";
import { buscarCategorias } from "../../../shared/services/api/categoria.api";
import styles from "./EditarProduto.module.scss";
import toast from "react-hot-toast";

interface Option {
  value: string;
  label: string;
}

interface ProdutoForm {
  name: string;
  categoria: string;
  loja: string;
  descricao?: string;
  estoque?: number;
  price?: number;
  status: "Em Estoque" | "Fora de Estoque" | "Encomendado";
  isActive: boolean;
}

export const EditarProduto: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [active, setActive] = useState(0);
  const [lojas, setLojas] = useState<Option[]>([]);
  const [categorias, setCategorias] = useState<Option[]>([]);
  const [imagens, setImagens] = useState<IPicture[]>([]);
  const [caracteristicas, setCaracteristicas] = useState<string[]>([]);
  const [combinations, setCombinations] = useState<ProductCombinationInput[]>(
    [],
  );
  const [inactiveCombinations, setInactiveCombinations] = useState<
    ProductCombinationInput[]
  >([]);
  const [selectedVariationOptions, setSelectedVariationOptions] = useState<
    Record<string, CombinationOption[]>
  >({});
  const [salvando, setSalvando] = useState(false);
  const [etapaSalvamento, setEtapaSalvamento] = useState<
    "salvando" | "imagens" | "atualizando" | ""
  >("");
  const [loading, setLoading] = useState(true);
  // Without the saved list, syncing an empty matrix would deactivate them all.
  const combinacoesCarregadasRef = useRef(false);

  const {
    register,
    watch,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<ProdutoForm>({
    defaultValues: {
      status: "Em Estoque",
      isActive: false,
    },
  });

  useEffect(() => {
    async function carregarProduto() {
      if (!id) {
        return;
      }

      try {
        setLoading(true);

        const produto = await buscarMeuProdutoPorId(id);

        if (!produto) {
          toast.error("Não foi possível carregar o produto.");
          return;
        }

        setValue("name", produto.name ?? "");
        setValue("loja", produto.lojaId ?? "");
        setValue("categoria", produto.categoriaId ?? "");
        setValue("descricao", produto.descricao ?? "");

        setValue("estoque", Number(produto.estoque ?? 0));

        setValue("price", Number(produto.price ?? 0));

        setValue("status", produto.status ?? "Em Estoque");

        setValue("isActive", Boolean(produto.isActive));

        setCaracteristicas(
          Array.isArray(produto.caracteristicas) ? produto.caracteristicas : [],
        );

        setImagens(
          (produto.gallery ?? []).map((url, index) => ({
            key: produto.galleryKeys?.[index],
            url,
          })),
        );

        const allCombinations = await listarCombinacoes(id, true);
        const existingCombinations = allCombinations.filter(
          (combination) => combination.isActive,
        );
        setCombinations(existingCombinations);
        setInactiveCombinations(
          allCombinations.filter((combination) => !combination.isActive),
        );
        combinacoesCarregadasRef.current = true;
        setSelectedVariationOptions(
          toSelectedVariationOptions(existingCombinations),
        );
      } catch (error) {
        console.error("Erro ao carregar produto:", error);

        toast.error("Não foi possível carregar o produto.");
      } finally {
        setLoading(false);
      }
    }

    carregarProduto();
  }, [id, setValue]);

  useEffect(() => {
    async function carregarLojas() {
      try {
        const data = await buscarLojasPorUsuario();

        setLojas(
          data.map((loja) => ({
            value: loja.id,
            label: loja.loja ?? "Loja sem nome",
          })),
        );
      } catch (error) {
        console.error("Erro ao carregar lojas:", error);

        if (isAxiosError(error)) {
          console.error("STATUS:", error.response?.status);
        }
      }
    }

    carregarLojas();
  }, []);

  useEffect(() => {
    async function carregarCategorias() {
      try {
        const data = await buscarCategorias();

        setCategorias(
          data.map((categoria) => ({
            value: categoria.id,
            label: categoria.nome,
          })),
        );
      } catch (error) {
        console.error(error);
      }
    }

    carregarCategorias();
  }, []);

  const onSubmit = async () => {
    if (!id || salvando) {
      return;
    }

    const data = getValues();

    if (!data.name?.trim()) {
      toast.error("Informe o nome do produto.");
      return;
    }

    if (!data.loja?.trim()) {
      toast.error("Selecione uma loja.");
      return;
    }

    if (!data.categoria?.trim()) {
      toast.error("Selecione uma categoria.");
      return;
    }

    if (!data.descricao?.trim()) {
      toast.error("Informe a descrição do produto.");
      return;
    }

    if (
      data.price === undefined ||
      data.price === null ||
      Number(data.price) <= 0
    ) {
      toast.error("O preço deve ser maior que zero.");
      return;
    }

    if (
      data.estoque === undefined ||
      data.estoque === null ||
      Number(data.estoque) <= 0
    ) {
      toast.error("O estoque deve ser maior que zero.");
      return;
    }

    if (imagens.length === 0) {
      toast.error("Adicione ao menos 1 imagem.");
      return;
    }

    if (caracteristicas.length === 0) {
      toast.error("Adicione ao menos 1 característica.");
      return;
    }

    const tiposPermitidos = ["image/jpeg", "image/png"];

    const imagemInvalida = imagens.find(
      (img) => img.file && !tiposPermitidos.includes(img.file.type),
    );

    if (imagemInvalida?.file) {
      toast.error(
        `A imagem "${imagemInvalida.file.name}" possui um formato não permitido. Use apenas JPG, JPEG ou PNG.`,
      );
      return;
    }

    try {
      setSalvando(true);
      setEtapaSalvamento("imagens");

      const gallery = await Promise.all(
        imagens.map(async (img) => {
          if (img.file) {
            const upload = await uploadImagemProduto(id, img.file);

            return upload.key;
          }

          return img.key;
        }),
      );

      const galleryKeys = gallery.filter((key): key is string => Boolean(key));

      setEtapaSalvamento("atualizando");

      await atualizarProduto(id, {
        name: data.name.trim(),
        descricao: data.descricao.trim(),
        price: Number(data.price),
        lojaId: data.loja,
        categoriaId: data.categoria,
        categoriaNome: data.categoria,
        estoque: Number(data.estoque),
        status: data.status,
        isActive: data.isActive,
        caracteristicas,
        gallery: galleryKeys,
      });

      if (combinacoesCarregadasRef.current) {
        await sincronizarCombinacoesProduto(id, combinations);
      }

      toast.success("Produto atualizado com sucesso!");

      navigate("/meusprodutos");
    } catch (error: any) {
      console.error("Erro ao atualizar produto:", error);

      const mensagem = error?.response?.data?.message;

      if (
        Array.isArray(mensagem) &&
        mensagem.some((msg: string) =>
          msg.includes("Only jpg, jpeg, png are allowed"),
        )
      ) {
        toast.error(
          "Formato de imagem não permitido. Use apenas JPG, JPEG ou PNG.",
        );
        return;
      }

      toast.error("Não foi possível atualizar o produto. Tente novamente.");
    } finally {
      setSalvando(false);
      setEtapaSalvamento("");
    }
  };

  if (loading) {
    return null;
  }

  return (
    <PageContainer title="Editar Produto" showDesktopSearch={false}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.page}>
          <Tabs active={active} setActive={setActive} />

          {active === 0 && (
            <Detalhes
              modo="edicao"
              register={register}
              errors={errors}
              lojas={lojas}
              categorias={categorias}
              watch={watch}
              setValue={setValue}
              status={watch("status")}
              isActive={watch("isActive")}
              onChangeStatus={(v) => setValue("status", v)}
              onChangeIsActive={(v) => setValue("isActive", v)}
            />
          )}

          {active === 1 && (
            <Caracteristicas
              register={register}
              errors={errors}
              imagens={imagens}
              setImagens={setImagens}
              caracteristicas={caracteristicas}
              setCaracteristicas={setCaracteristicas}
              categoryId={watch("categoria") ?? ""}
              selectedVariationOptions={selectedVariationOptions}
              setSelectedVariationOptions={setSelectedVariationOptions}
            />
          )}

          {active === 2 && (
            <Combinacoes
              selectedVariationOptions={selectedVariationOptions}
              combinations={combinations}
              setCombinations={setCombinations}
              inactiveCombinations={inactiveCombinations}
            />
          )}

          <div className={styles.containerBtn}>
            {active === 0 ? (
              <>
                <button
                  type="button"
                  onClick={() => navigate("/meusprodutos")}
                  className={styles.btnCancelar}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => setActive(1)}
                  className={styles.btnSalvar}
                  disabled={salvando}
                >
                  Avançar
                </button>
              </>
            ) : active === 1 ? (
              <>
                <button
                  type="button"
                  onClick={() => setActive(0)}
                  className={styles.btnCancelar}
                >
                  Voltar
                </button>
                <button
                  key="avancar-combinacoes"
                  type="button"
                  onClick={() => setActive(2)}
                  className={styles.btnSalvar}
                  disabled={salvando}
                >
                  Avançar
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setActive(1)}
                  className={styles.btnCancelar}
                >
                  Voltar
                </button>
                <button
                  key="salvar-produto"
                  type="submit"
                  className={`${styles.btnSalvar} ${salvando ? styles.salvando : ""}`}
                  disabled={salvando}
                >
                  {salvando && <span className={styles.spinner} />}
                  {etapaSalvamento === "imagens"
                    ? "Enviando imagens..."
                    : etapaSalvamento === "atualizando"
                      ? "Atualizando produto..."
                      : salvando
                        ? "Salvando..."
                        : "Salvar Alterações"}
                </button>
              </>
            )}
          </div>
        </div>
      </form>
    </PageContainer>
  );
};
